/**
 * <StudySession> — the distraction-free card session view (`/study/session`).
 * Loads one card at a time, reveals on click, submits SM-2 rating, auto-
 * advances to the next card.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Flashcard } from '../components/study/Flashcard';
import { RATING_META, STUDY_THEME, paperBackground } from '../components/study/studyTheme';
import { getNextCard, revealCard, submitReview } from '../lib/studyApi';

export default function StudySession() {
  const [params] = useSearchParams();
  const deckId = params.get('deck');
  const focus = params.get('focus') || null;   // G2.1 · 'weakest' bias
  const navigate = useNavigate();

  const [card, setCard] = useState(null);
  const [reason, setReason] = useState('');
  const [back, setBack] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [stats, setStats] = useState({ seen: 0, correct: 0 });
  const [empty, setEmpty] = useState(false);

  const loadNext = useCallback(() => {
    setLoading(true);
    setRevealed(false);
    setBack('');
    setErr('');
    getNextCard(deckId, focus)
      .then((data) => {
        if (!data.card) {
          setEmpty(true);
          setCard(null);
        } else {
          setEmpty(false);
          setCard(data.card);
          setReason(data.reason || '');
        }
      })
      .catch((e) => setErr(e.message || 'Failed to load next card'))
      .finally(() => setLoading(false));
  }, [deckId, focus]);

  useEffect(() => { loadNext(); }, [loadNext]);

  const doReveal = useCallback(() => {
    if (!card) return;
    revealCard(card.id)
      .then((d) => {
        setBack(d.back);
        setRevealed(true);
      })
      .catch(() => setBack('(failed to fetch answer)'));
  }, [card]);

  const doRate = useCallback((rating) => {
    if (!card) return;
    submitReview(card.id, rating)
      .then(() => {
        setStats((s) => ({
          seen: s.seen + 1,
          correct: s.correct + (rating === 'good' || rating === 'easy' ? 1 : 0),
        }));
        loadNext();
      })
      .catch((e) => setErr(e.message || 'Rating failed'));
  }, [card, loadNext]);

  // Keyboard shortcuts: Space = reveal; 1/2/3/4 = ratings; Esc = exit
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === ' ' && !revealed && card) { e.preventDefault(); doReveal(); return; }
      if (e.key === 'Escape') { navigate('/study'); return; }
      if (revealed) {
        const match = RATING_META.find((r) => r.key === e.key);
        if (match) { e.preventDefault(); doRate(match.rating); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [revealed, card, doReveal, doRate, navigate]);

  return (
    <div style={paperBackground()} data-testid="study-session-page">
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 20px' }}>
        {/* Top bar */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 32,
        }}>
          <button
            onClick={() => navigate('/study')}
            data-testid="session-exit-btn"
            style={{
              padding: '8px 14px', fontSize: 13, fontWeight: 600,
              background: 'transparent', color: STUDY_THEME.inkSoft,
              border: `1px solid ${STUDY_THEME.line}`, borderRadius: 6,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            ← Deck Hub
          </button>
          <div style={{
            fontSize: 13, color: STUDY_THEME.inkSoft, fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
          }} data-testid="session-stats">
            <span style={{ color: STUDY_THEME.goldDeep }}>{stats.seen}</span> cards ·
            {' '}
            <span style={{ color: '#16A34A' }}>{stats.correct}</span> good
          </div>
          {reason && card && (
            <div style={{
              fontSize: 11, color: STUDY_THEME.muted, letterSpacing: 1,
              textTransform: 'uppercase', fontWeight: 700,
            }} data-testid="session-reason">
              {reason === 'overdue' ? '⏰ Overdue' :
               reason === 'fresh'   ? '✨ New card' :
               reason === 'ahead'   ? '⏩ Ahead of schedule' : reason}
            </div>
          )}
        </div>

        {loading && (
          <div style={{ padding: 40, textAlign: 'center', color: STUDY_THEME.muted }}>
            Loading next card…
          </div>
        )}
        {err && (
          <div style={{
            padding: 20, borderRadius: 10, background: '#FEE2E2',
            color: '#991B1B', marginBottom: 20,
          }} data-testid="session-error">{err}</div>
        )}
        {empty && !loading && (
          <div style={{
            background: 'white', padding: 60, borderRadius: 14,
            textAlign: 'center', boxShadow: STUDY_THEME.shadow,
            maxWidth: 560, margin: '40px auto',
          }} data-testid="session-empty">
            <div style={{ fontSize: 48, marginBottom: 20 }}>🎯</div>
            <h2 style={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: 28, margin: '0 0 12px',
            }}>
              You&apos;re all caught up.
            </h2>
            <p style={{ color: STUDY_THEME.inkSoft, lineHeight: 1.6, margin: '0 0 24px' }}>
              No cards are due right now. Cards you rated Good or Easy will
              return at their scheduled interval. In the meantime, want to
              duel the AI on this deck?
            </p>
            <button
              onClick={() => navigate(deckId ? `/study/duel?deck=${deckId}` : '/study')}
              data-testid="session-empty-duel-btn"
              style={{
                padding: '12px 24px', fontSize: 14, fontWeight: 700,
                background: STUDY_THEME.gold, color: STUDY_THEME.ink,
                border: `1px solid ${STUDY_THEME.goldDark}`, borderRadius: 8,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              ⚔️ Start a Duel
            </button>
          </div>
        )}
        {card && !loading && (
          <Flashcard
            card={card}
            revealed={revealed}
            back={back}
            onFlip={doReveal}
            onRate={doRate}
            ratings={revealed ? RATING_META : null}
          />
        )}

        {/* Keyboard hints */}
        {card && (
          <div style={{
            marginTop: 20, textAlign: 'center', fontSize: 12,
            color: STUDY_THEME.muted, letterSpacing: 0.5,
          }}>
            {!revealed
              ? 'Press [Space] to reveal · [Esc] to exit'
              : 'Press [1] Again · [2] Hard · [3] Good · [4] Easy'}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * G3.1 — End-of-session reflection prompt.
 * When the session runs out of due cards we now ask the student a one-line
 * "what clicked / what still feels shaky?" question and persist it to the
 * `study_reflections` collection. This is Loble strategy #3 in practice
 * (metacognition) and it also feeds the weekly digest (G3.3).
 */
function ReflectionCard({ deckId, navigate }) {
  const [text, setText] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [err, setErr] = React.useState('');

  const submit = async () => {
    const clean = (text || '').trim();
    if (!clean || saving) return;
    setSaving(true); setErr('');
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token') || '';
      const r = await fetch(`${API_URL}/api/study/reflections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ deck_id: deckId || null, text: clean }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setSaved(true);
    } catch (e) {
      setErr(e.message || 'Failed to save reflection');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      background: 'white', padding: 40, borderRadius: 14,
      textAlign: 'left', boxShadow: STUDY_THEME.shadow,
      maxWidth: 620, margin: '40px auto',
    }} data-testid="session-empty">
      <div style={{ fontSize: 40, marginBottom: 12, textAlign: 'center' }}>🎯</div>
      <h2 style={{
        fontFamily: '"Playfair Display", Georgia, serif',
        fontSize: 26, margin: '0 0 8px', textAlign: 'center',
      }}>
        You&apos;re all caught up.
      </h2>
      <p style={{ color: STUDY_THEME.inkSoft, lineHeight: 1.55, margin: '0 0 20px', textAlign: 'center', fontSize: 14 }}>
        Before you close the tab — a quick 20-second reflection makes tomorrow&apos;s recall roughly 30 % stronger. That&apos;s the neuroscience.
      </p>
      {!saved ? (
        <>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: STUDY_THEME.ink, marginBottom: 6 }}>
            In 20 words: what clicked, and what still feels shaky?
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Bias-variance clicked once I imagined it as underfit vs overfit; regularization still feels shaky."
            data-testid="reflection-input"
            rows={3}
            maxLength={800}
            style={{
              width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${STUDY_THEME.muted}`,
              fontSize: 14, fontFamily: 'inherit', resize: 'vertical', outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ fontSize: 11, color: STUDY_THEME.muted, marginTop: 4, textAlign: 'right' }}>
            {text.length}/800
          </div>
          {err && (
            <div style={{ color: '#B91C1C', fontSize: 12, marginTop: 6 }}>{err}</div>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate(deckId ? `/study/duel?deck=${deckId}` : '/study')}
              data-testid="session-empty-duel-btn"
              style={{
                padding: '10px 18px', fontSize: 13, fontWeight: 600,
                background: 'white', color: STUDY_THEME.ink,
                border: `1px solid ${STUDY_THEME.muted}`, borderRadius: 8,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Skip · Start a Duel ⚔️
            </button>
            <button
              onClick={submit}
              disabled={saving || !text.trim()}
              data-testid="reflection-submit-btn"
              style={{
                padding: '10px 22px', fontSize: 13, fontWeight: 700,
                background: STUDY_THEME.gold, color: STUDY_THEME.ink,
                border: `1px solid ${STUDY_THEME.goldDark}`, borderRadius: 8,
                cursor: saving || !text.trim() ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                opacity: saving || !text.trim() ? 0.5 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Save reflection & finish'}
            </button>
          </div>
        </>
      ) : (
        <div data-testid="reflection-saved" style={{ textAlign: 'center', padding: '10px 0' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🧠</div>
          <div style={{ fontWeight: 700, marginBottom: 6, color: STUDY_THEME.ink }}>Saved.</div>
          <div style={{ fontSize: 13, color: STUDY_THEME.inkSoft, marginBottom: 18, lineHeight: 1.5 }}>
            Your tutor will open with this next time so you don&apos;t lose the thread.
          </div>
          <button
            onClick={() => navigate('/study')}
            style={{
              padding: '10px 22px', fontSize: 13, fontWeight: 700,
              background: STUDY_THEME.gold, color: STUDY_THEME.ink,
              border: `1px solid ${STUDY_THEME.goldDark}`, borderRadius: 8,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Back to Study Hub
          </button>
        </div>
      )}
    </div>
  );
}
